import React, { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Divider, keyframes, styled } from "@mui/material";
import { toReadable } from "../../utils/methods";
import { TERM_UNIT } from "../../utils/constants";
import { PulseCircle } from "../../components";

const MONO_FONT = "'IBM Plex Mono', monospace";
const BODY_FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

export type TierLike = {
  title: string;
  description?: string;
  perks?: string[];
  price?: string | number;
  details?: string[];
  active?: boolean;
};

const CardRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#f2f2f2",
  borderRadius: "20px",
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "18px",
    gap: "14px",
  },
}));

const TierPillRow = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "center",
  overflowX: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

const TierPill = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  minWidth: "64px",
  padding: "7px 17px",
  borderRadius: "15px",
  border: `1px solid ${selected ? "transparent" : "#ccc"}`,
  backgroundColor: selected ? "#ccc" : "transparent",
  fontFamily: MONO_FONT,
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16.8px",
  letterSpacing: "0.084px",
  textTransform: "uppercase",
  color: selected ? "#000" : "#999",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  transition: "background-color 0.15s ease, color 0.15s ease",
}));

const ActiveDot = styled("span")({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#8cc63f",
  flexShrink: 0,
});

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const AnimatedTierContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  animation: `${slideInFromLeft} 0.8s ease`,
});

const SectionLabel = styled(Box)({
  fontFamily: "monospace",
  textTransform: "uppercase",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "18px",
  color: "#000",
});

const PlanText = styled(Box)({
  fontFamily: BODY_FONT,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#000",
  whiteSpace: "pre-wrap",
});

const TierBody = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "15px",
  [theme.breakpoints.down("sm")]: {
    gap: "12px",
  },
}));

const TierMetaCol = styled(Box)(({ theme }) => ({
  fontFamily: MONO_FONT,
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#000",
  textTransform: "uppercase",
  flexShrink: 0,
  width: "auto",
  minWidth: "92px",
  [theme.breakpoints.down("sm")]: {
    minWidth: "77px",
  },
}));

const TierMetaTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
  color: active ? "#8cc63f" : "#999",
}));

const TierMetaDuration = styled(Box)({
  color: "#999",
});

const PerksList = styled("ul")(({ theme }) => ({
  margin: 0,
  paddingLeft: "21px",
  listStyle: "disc",
  fontFamily: BODY_FONT,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#000",
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    paddingLeft: "18px",
  },
  "& li": {
    marginBottom: 0,
  },
}));

type Props = {
  tiers: TierLike[];
  contractPeriod?: number;
};

const LandDescriptionTiersCard: React.FC<Props> = ({ tiers, contractPeriod }) => {
  const safeTiers = useMemo(() => (Array.isArray(tiers) ? tiers : []), [tiers]);

  const defaultIndex = useMemo(() => {
    if (safeTiers.length === 0) return 0;
    const lastActive = [...safeTiers].reverse().findIndex((tier) => tier?.active);
    if (lastActive === -1) return 0;
    return safeTiers.length - 1 - lastActive;
  }, [safeTiers]);

  const [selectedIndex, setSelectedIndex] = useState<number>(defaultIndex);

  useEffect(() => {
    setSelectedIndex(defaultIndex);
  }, [defaultIndex]);

  const selectedTier = safeTiers[selectedIndex];

  const perks = useMemo(() => {
    if (!selectedTier) return [];
    if (Array.isArray(selectedTier.perks) && selectedTier.perks.length > 0) {
      return selectedTier.perks.filter((perk) => typeof perk === "string" && perk.trim().length > 0);
    }
    if (Array.isArray(selectedTier.details) && selectedTier.details.length > 0) {
      return selectedTier.details
        .slice(1)
        .filter((detail) => typeof detail === "string" && detail.trim().length > 0) as string[];
    }
    return [];
  }, [selectedTier]);

  const priceLabel = useMemo(() => {
    if (!selectedTier || selectedTier.price === undefined || selectedTier.price === null) return "—";
    return `$ ${toReadable(selectedTier.price as string | number)}`;
  }, [selectedTier]);

  const durationLabel = useMemo(() => {
    if (!contractPeriod || !Number.isFinite(Number(contractPeriod))) return null;
    const value = Number(contractPeriod);
    const unit = value > 1 ? TERM_UNIT : TERM_UNIT.slice(0, -1);
    return `${value} ${unit}`.toUpperCase();
  }, [contractPeriod]);

  if (safeTiers.length === 0) return null;

  return (
    <CardRoot>
      <TierPillRow>
        {safeTiers.map((tier, index) => (
          <TierPill
            key={`${tier.title}-${index}`}
            selected={index === selectedIndex}
            onClick={() => setSelectedIndex(index)}
          >
            {tier.active && <ActiveDot />}
            {tier.title}
          </TierPill>
        ))}
      </TierPillRow>

      {selectedTier && (
        <AnimatedTierContent key={selectedIndex}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <SectionLabel>Plan</SectionLabel>
            <Divider sx={{ borderColor: "#000", borderBottomWidth: "1px", opacity: 1 }} />
            <PlanText sx={{ mt: "12px" }}>
              {selectedTier.description && selectedTier.description.trim().length > 0
                ? selectedTier.description
                : "No plan available"}
            </PlanText>
          </Box>
          <TierBody>
            {selectedTier.active ? (
              <PulseCircle sold sx={{ mt: "2px" }} />
            ) : (
              <Box sx={{ width: "16px", height: "16px", marginRight: "6px", flexShrink: 0 }} />
            )}
            <TierMetaCol>
              <TierMetaTitle active={!!selectedTier.active}>{selectedTier.title}</TierMetaTitle>
              <Box>{priceLabel}</Box>
              {durationLabel && <TierMetaDuration>{durationLabel}</TierMetaDuration>}
            </TierMetaCol>
            {perks.length > 0 && (
              <PerksList>
                {perks.map((perk, index) => (
                  <li key={index}>{perk}</li>
                ))}
              </PerksList>
            )}
          </TierBody>
        </AnimatedTierContent>
      )}
    </CardRoot>
  );
};

export default LandDescriptionTiersCard;
